import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, Check, Loader2, Sparkles } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { tenantPath } from "@/lib/urls";
import { useTenantStore, type TenantSummary } from "@/stores/tenantStore";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api/requests";
import { useSession } from "@/lib/hooks/useSession";
import { getClientTimezone } from "@/lib/utils/timezone";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { WORKSPACE_TIMEZONE_OPTIONS, createWorkspaceSchema } from "@/components/TenantSwitcher.schema";

const formatRoleLabel = (role: string | null | undefined, t: (key: string, options?: Record<string, unknown>) => string): string => {
  if (!role) {
    return t("tenantSwitcher.roles.unknown", { defaultValue: "Staff" });
  }
  const normalized = role.toLowerCase();
  return t(`tenantSwitcher.roles.${normalized}`, {
    defaultValue: role.charAt(0).toUpperCase() + role.slice(1),
  });
};

const sortByRecency = (tenants: TenantSummary[]): TenantSummary[] => {
  return [...tenants].sort((a, b) => {
    const aValue = a.lastUsedAt ? Date.parse(a.lastUsedAt) : 0;
    const bValue = b.lastUsedAt ? Date.parse(b.lastUsedAt) : 0;
    return bValue - aValue;
  });
};

type TenantSwitcherVariant = "standard" | "icon";

interface TenantSwitcherProps {
  className?: string;
  variant?: TenantSwitcherVariant;
}

export function TenantSwitcher({ className, variant = "standard" }: TenantSwitcherProps): JSX.Element | null {
  const { t } = useTranslation(["common", "dashboard"]);
  const location = useLocation();
  const navigate = useNavigate();
  const { tenants, activeRestaurantId, activeSubdomain, activeRestaurantName, setActiveTenant } = useTenantStore();
  const session = useSession();
  const { toast } = useToast();
  const [switchingTenantId, setSwitchingTenantId] = React.useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const triggerSessionRefetch = session.triggerRefetch;

  const roster = React.useMemo(() => {
    const normalized = tenants.length > 0 ? tenants : [];
    const hasActive = normalized.some((tenant) => tenant.id === activeRestaurantId);

    if (!hasActive && activeRestaurantId && activeSubdomain) {
      return sortByRecency([
        ...normalized,
        {
          id: activeRestaurantId,
          slug: activeSubdomain,
          name: activeRestaurantName ?? activeSubdomain,
          role: null,
          status: null,
          lastUsedAt: new Date().toISOString(),
          isDemo: false,
        },
      ]);
    }

    return sortByRecency(normalized);
  }, [tenants, activeRestaurantId, activeSubdomain, activeRestaurantName]);

  const activeTenant =
    roster.find((tenant) => tenant.id === activeRestaurantId) ??
    (roster.length > 0 ? roster[0] : null);

  const buildDestination = React.useCallback(
    (slug: string) => {
      const { pathname, search, hash } = location;
      const match = pathname.match(/^\/r\/([^/]+)(\/.*)?$/);
      let nextPath: string;
      if (match) {
        const trailing = match[2] ?? "";
        nextPath = `/r/${slug}${trailing}`;
      } else {
        const trimmed = pathname.replace(/^\/+/, "");
        nextPath = tenantPath(slug, trimmed);
      }
      return `${nextPath}${search}${hash}`;
    },
    [location],
  );

  const handleSelect = React.useCallback(
    async (tenant: TenantSummary) => {
      if (tenant.id === activeTenant?.id) {
        return;
      }
      if (switchingTenantId) {
        return;
      }

      setSwitchingTenantId(tenant.id);

      try {
        const response = await apiFetch('/tenant/select', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ tenantId: tenant.id }),
        });

        const payload = (await response.json().catch(() => null)) as
          | { ok?: boolean; tenant?: { id: string; slug: string; name?: string | null }; message?: string }
          | null;

        if (!response.ok || !payload?.ok || !payload.tenant) {
          const message =
            payload?.message ??
            t('tenantSwitcher.switchError', {
              defaultValue: 'Something went wrong while switching workspaces.',
            });
          throw new Error(message);
        }

        const nextTenant = payload.tenant;

        setActiveTenant(
          tenant.id,
          nextTenant.slug,
          nextTenant.name ?? nextTenant.slug,
          tenant.isDemo ?? false,
        );

        navigate(buildDestination(nextTenant.slug));

        if (typeof triggerSessionRefetch === 'function') {
          await triggerSessionRefetch().catch((error) => {
            if (import.meta.env.DEV) {
              console.warn('[TenantSwitcher] session refetch after workspace switch failed', error);
            }
          });
        }
      } catch (error) {
        console.error('[TenantSwitcher] failed to switch workspace', error);
        toast({
          variant: 'destructive',
          title: t('tenantSwitcher.switchErrorTitle', { defaultValue: 'Unable to switch workspace' }),
          description:
            error instanceof Error
              ? error.message
              : t('tenantSwitcher.switchError', {
                  defaultValue: 'Something went wrong while switching workspaces.',
                }),
        });
      } finally {
        setSwitchingTenantId(null);
      }
    },
    [
      activeTenant?.id,
      buildDestination,
      navigate,
      setActiveTenant,
      switchingTenantId,
      t,
      toast,
      triggerSessionRefetch,
    ],
  );

  const handleWorkspaceCreated = React.useCallback(
    (tenant: { id: string; slug: string; name?: string | null }) => {
      setActiveTenant(tenant.id, tenant.slug, tenant.name ?? tenant.slug, false);
      if (typeof triggerSessionRefetch === "function") {
        triggerSessionRefetch()
          .catch((error) => {
            if (import.meta.env.DEV) {
              console.warn("[TenantSwitcher] failed to refetch session after workspace creation", error);
            }
          });
      }
    },
    [setActiveTenant, triggerSessionRefetch],
  );

  const canCreateWorkspace = React.useMemo(() => {
    if (!session.user?.email) {
      return false;
    }
    const perms = session.permissions ?? [];
    return perms.includes("settings.manage") || perms.includes("staff.manage");
  }, [session.permissions, session.user?.email]);

  if (!activeTenant) {
    return null;
  }

  const hasMultiple = roster.length > 1;
  const isIconVariant = variant === "icon";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size={isIconVariant ? "icon" : "sm"}
            className={cn(
              isIconVariant
                ? "h-9 w-9 shrink-0 rounded-lg border border-gray-200/70 bg-white/80 text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:ring-emerald-500/60 dark:border-gray-700/60 dark:bg-gray-900/80 dark:text-gray-200 dark:hover:bg-gray-800"
                : "inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200/70 bg-white/80 px-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-emerald-500/60 dark:border-gray-700/60 dark:bg-gray-900/80 dark:text-gray-200 dark:hover:bg-gray-800 sm:px-3",
              className,
            )}
            aria-label={t("tenantSwitcher.label", { defaultValue: "Switch workspace" })}
          >
            <Building2 className="h-4 w-4 text-emerald-500 dark:text-emerald-400" aria-hidden />
            {isIconVariant ? (
              <span className="sr-only">
                {`${t("tenantSwitcher.label", { defaultValue: "Switch workspace" })} (${
                  activeTenant.name ?? activeTenant.slug
                })`}
              </span>
            ) : (
              <>
                <span className="hidden max-w-[10rem] truncate sm:inline">
                  {activeTenant.name ?? activeTenant.slug}
                </span>
                <span className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {formatRoleLabel(activeTenant.role, (key, options) => t(key, options))}
                </span>
                {activeTenant.isDemo && (
                  <Badge
                    variant="secondary"
                    className="hidden h-5 items-center gap-1 rounded-md px-2 text-[11px] font-medium sm:flex"
                  >
                    <Sparkles className="h-3 w-3 text-emerald-500 dark:text-emerald-300" aria-hidden />
                    {t("tenantSwitcher.badges.demo", { defaultValue: "Demo" })}
                  </Badge>
                )}
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-80 border-gray-200/70 bg-white/95 p-2 text-gray-700 backdrop-blur-md dark:border-gray-700/60 dark:bg-gray-900/95 dark:text-gray-200"
        >
        <DropdownMenuLabel className="px-3 pb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t("tenantSwitcher.label", { defaultValue: "Switch workspace" })}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {roster.map((tenant) => {
          const isActive = tenant.id === activeTenant.id;
          const isSwitching = switchingTenantId === tenant.id;
          const isBusy = Boolean(switchingTenantId && switchingTenantId !== tenant.id);
          return (
            <DropdownMenuItem
              key={tenant.id}
              disabled={isActive || isBusy || !hasMultiple}
              onSelect={async () => {
                await handleSelect(tenant);
              }}
              className={cn(
                "flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-200",
                isActive
                  ? "bg-emerald-50 text-emerald-700 shadow-sm dark:bg-emerald-500/10 dark:text-emerald-400"
                  : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-gray-50",
              )}
            >
              <div className="flex flex-col">
                <span className="font-semibold leading-tight">{tenant.name ?? tenant.slug}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatRoleLabel(tenant.role, (key, options) => t(key, options))}
                </span>
                {tenant.status && (
                  <span className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {tenant.status}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {tenant.isDemo && (
                  <Badge variant="outline" className="h-5 items-center gap-1 rounded-md px-2 text-[11px] font-medium">
                    {t("tenantSwitcher.badges.demo", { defaultValue: "Demo" })}
                  </Badge>
                )}
                {isSwitching ? (
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-500 dark:text-emerald-400" aria-hidden />
                ) : (
                  isActive && <Check className="h-4 w-4 text-emerald-500 dark:text-emerald-400" aria-hidden />
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
        {!hasMultiple && (
          <>
            <DropdownMenuSeparator />
            <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
              {t("tenantSwitcher.empty", { defaultValue: "No other workspaces assigned." })}
            </div>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          asChild
          className="cursor-pointer rounded-lg px-2 py-2 focus:bg-gray-100/80 focus:text-gray-900 dark:focus:bg-gray-800/80 dark:focus:text-gray-100"
          onSelect={(event) => {
            event.preventDefault();
            if (canCreateWorkspace) {
              setIsCreateOpen(true);
            }
          }}
        >
          <div className="flex flex-col gap-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="justify-start rounded-md px-3 text-sm font-medium text-emerald-700 hover:text-emerald-800 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-70 dark:text-emerald-300 dark:hover:text-emerald-200"
              disabled={!canCreateWorkspace}
            >
              {t("tenantSwitcher.addWorkspace.cta", { defaultValue: "Add workspace" })}
            </Button>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {canCreateWorkspace
                ? t("tenantSwitcher.addWorkspace.subtitle", {
                    defaultValue: "Create a new location or concept.",
                  })
                : t("tenantSwitcher.addWorkspace.permissionHint", {
                    defaultValue: "Only owners or managers can add workspaces.",
                  })}
            </p>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
      </DropdownMenu>
      <CreateWorkspaceDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onWorkspaceCreated={handleWorkspaceCreated}
        userEmail={session.user?.email ?? null}
      />
    </>
  );
}

export default TenantSwitcher;

type CreateWorkspaceDialogProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onWorkspaceCreated?: (tenant: { id: string; slug: string; name?: string | null }) => void;
  userEmail: string | null;
};

type WorkspaceFormValues = {
  name: string;
  slug: string;
  timezone: string;
};

type CreateWorkspaceResponse =
  | {
      ok: true;
      tenant: { id: string; slug: string; name: string | null };
      message?: string;
    }
  | {
      ok: false;
      message?: string;
    };

const CreateWorkspaceDialog = ({
  open,
  onOpenChange,
  onWorkspaceCreated,
  userEmail,
}: CreateWorkspaceDialogProps): JSX.Element => {
  const { t } = useTranslation(["common", "dashboard"]);
  const { toast } = useToast();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const defaultTimezone = React.useMemo(() => {
    const clientTimezone = getClientTimezone();
    if (clientTimezone && WORKSPACE_TIMEZONE_OPTIONS.some((option) => option.value === clientTimezone)) {
      return clientTimezone;
    }
    return WORKSPACE_TIMEZONE_OPTIONS[0]?.value ?? "America/Los_Angeles";
  }, []);

  const formSchema = React.useMemo(
    () =>
      createWorkspaceSchema({
        nameError: t("tenantSwitcher.addWorkspace.nameError", {
          defaultValue: "Enter a workspace name.",
        }),
        nameLengthError: t("tenantSwitcher.addWorkspace.nameLengthError", {
          defaultValue: "Workspace name must be 160 characters or fewer.",
        }),
        slugError: t("tenantSwitcher.addWorkspace.slugError", {
          defaultValue: "Use 3-48 lowercase letters, numbers, or dashes, or leave blank.",
        }),
        timezoneError: t("tenantSwitcher.addWorkspace.timezoneError", {
          defaultValue: "Select a timezone.",
        }),
      }),
    [t],
  );

  const form = useForm<WorkspaceFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      slug: "",
      timezone: defaultTimezone,
    },
  });

  const closeDialog = React.useCallback(
    (next: boolean) => {
      if (!next) {
        if (isSubmitting) {
          return;
        }
        form.reset({
          name: "",
          slug: "",
          timezone: defaultTimezone,
        });
        setSubmitError(null);
      }
      onOpenChange(next);
    },
    [defaultTimezone, form, isSubmitting, onOpenChange],
  );

  const onSubmit = form.handleSubmit(async (values) => {
    if (isSubmitting) {
      return;
    }

    if (!userEmail) {
      const missingEmailMessage = t("tenantSwitcher.addWorkspace.missingEmail", {
        defaultValue: "We couldn't determine your account email. Contact an administrator for assistance.",
      });
      setSubmitError(missingEmailMessage);
      toast({
        variant: "destructive",
        title: t("tenantSwitcher.addWorkspace.errorTitle", {
          defaultValue: "Unable to create workspace",
        }),
        description: missingEmailMessage,
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    const payload = {
      mode: "autoprovision",
      email: userEmail,
      restaurantName: values.name.trim(),
      tenantSlug: values.slug.length > 0 ? values.slug : undefined,
      tz: values.timezone,
    };

    try {
      const response = await apiFetch("/onboarding/attach-tenant", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const json = (await response.json().catch(() => null)) as CreateWorkspaceResponse | null;

      if (!response.ok || !json || json.ok !== true || !json.tenant) {
        const errorMessage =
          json?.message ??
          t("tenantSwitcher.addWorkspace.unknownError", {
            defaultValue: "Something went wrong while creating the workspace. Try again.",
          });
        throw new Error(errorMessage);
      }

      toast({
        title: t("tenantSwitcher.addWorkspace.successTitle", {
          defaultValue: "Workspace added",
        }),
        description: t("tenantSwitcher.addWorkspace.successDescription", {
          defaultValue: "{{name}} is ready. You are viewing the new workspace.",
          name: json.tenant.name ?? json.tenant.slug,
        }),
      });

      onWorkspaceCreated?.(json.tenant);
      closeDialog(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("tenantSwitcher.addWorkspace.unknownError", {
              defaultValue: "Something went wrong while creating the workspace. Try again.",
            });
      setSubmitError(message);
      toast({
        variant: "destructive",
        title: t("tenantSwitcher.addWorkspace.errorTitle", {
          defaultValue: "Unable to create workspace",
        }),
        description: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent
        className="border-gray-200/70 bg-white/95 text-gray-700 dark:border-gray-700/60 dark:bg-gray-900/95 dark:text-gray-200"
        onEscapeKeyDown={(event) => {
          if (isSubmitting) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (isSubmitting) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t("tenantSwitcher.addWorkspace.title", { defaultValue: "Create a new workspace" })}
          </DialogTitle>
          <DialogDescription>
            {t("tenantSwitcher.addWorkspace.description", {
              defaultValue: "Launch a new location or concept. We’ll copy your branding automatically.",
            })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">
              {t("tenantSwitcher.addWorkspace.nameLabel", { defaultValue: "Workspace name" })}
            </Label>
            <Input
              id="workspace-name"
              placeholder={t("tenantSwitcher.addWorkspace.namePlaceholder", {
                defaultValue: "e.g., Market Street Pop-Up",
              })}
              {...form.register("name")}
              disabled={isSubmitting}
            />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="workspace-slug">
              {t("tenantSwitcher.addWorkspace.slugLabel", { defaultValue: "Subdomain (optional)" })}
            </Label>
            <Input
              id="workspace-slug"
              placeholder={t("tenantSwitcher.addWorkspace.slugPlaceholder", {
                defaultValue: "e.g., market-street",
              })}
              {...form.register("slug")}
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("tenantSwitcher.addWorkspace.slugHint", {
                defaultValue: "Lowercase letters, numbers, or dashes. Leave blank to auto-generate.",
              })}
            </p>
            {form.formState.errors.slug && (
              <p className="text-xs text-destructive">{form.formState.errors.slug.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="workspace-timezone">
              {t("tenantSwitcher.addWorkspace.timezoneLabel", { defaultValue: "Timezone" })}
            </Label>
            <Select
              value={form.watch("timezone")}
              onValueChange={(value) => form.setValue("timezone", value, { shouldValidate: true })}
              disabled={isSubmitting}
            >
              <SelectTrigger id="workspace-timezone">
                <SelectValue
                  placeholder={t("tenantSwitcher.addWorkspace.timezonePlaceholder", {
                    defaultValue: "Select timezone",
                  })}
                />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {WORKSPACE_TIMEZONE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.timezone && (
              <p className="text-xs text-destructive">{form.formState.errors.timezone.message}</p>
            )}
          </div>
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => closeDialog(false)}
              disabled={isSubmitting}
            >
              {t("tenantSwitcher.addWorkspace.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" disabled={isSubmitting} className="inline-flex items-center gap-2">
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              <span>
                {t("tenantSwitcher.addWorkspace.submit", { defaultValue: "Create workspace" })}
              </span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { CreateWorkspaceDialog };
